import html2pdf from 'html2pdf.js';
import apiService from '../config';

export async function generateCompensationPDF(params, isPreview = false) {
  try {
    console.log("Generating Compensation Notice - Section 37(2) with html2pdf");
    console.log("Params:", params);
    console.log("Is Preview:", isPreview);
    
    // Validate required parameters
    if (!params.projectId || !params.districtId || !params.talukaId || !params.villageId) {
      throw new Error("Missing required parameters for PDF generation");
    }

    // Fetch compensation data from API
    console.log("Fetching compensation data...");
    const response = await apiService.getLandHolding(
      params.projectId,
      params.districtId,
      params.talukaId,
      params.villageId
    );

    console.log("API Response:", response);
    const compensationData = response?.data || [];
    console.log("Compensation Data:", compensationData);
    
    if (compensationData.length === 0) {
      console.warn("No compensation data found, creating sample data for preview");
      // For preview purposes, create sample data if none exists
      const sampleData = [{
        landCompensation: 0,
        treeCompensation: 0,
        structureCompensation: 0,
        wellCompensation: 0,
        otherCompensation: 0,
        totalCompensation: 0,
        solatium30: 0,
        interestAward: 0,
        additionalCompensation: 0,
        totalFinalAward: 0,
        finalPayableAmount: 0
      }];
      
      if (!isPreview) {
        throw new Error("No compensation data found for selected criteria");
      } else {
        console.log("Using sample data for preview");
        compensationData.push(...sampleData);
      }
    }

    // Get district/taluka/village names
    console.log("Fetching location details...");
    const locationDetails = await getLocationDetails(params);
    console.log("Location Details:", locationDetails);
    
    // Calculate totals from real data
    console.log("Calculating totals...");
    const totals = calculateCompensationTotals(compensationData);
    console.log("Calculated Totals:", totals);
    
    if (isPreview) {
      console.log("Generating HTML preview...");
      const htmlPreview = await generateCompensationHTML(compensationData, totals, locationDetails, params);
      console.log("Generated HTML preview:", htmlPreview ? "Success" : "Failed");
      return htmlPreview;
    } else {
      console.log("Generating PDF download...");
      return await generateAndDownloadPDF(compensationData, totals, locationDetails, params);
    }
    
  } catch (error) {
    console.error("Error generating compensation notice:", error);
    console.error("Error stack:", error.stack);
    throw error;
  }
}

// Calculate compensation totals from API data
function calculateCompensationTotals(data) {
  console.log("Calculating totals for data:", data);
  
  const totals = {
    // Row 1: Base market value (half of total land compensation)
    baseMarketValue: data.reduce((sum, item) => sum + (item.landCompensation || 0), 0) / 2,
    
    // Row 3: Land value (market value × multiplier factor of 2)
    landValue: data.reduce((sum, item) => sum + (item.landCompensation || 0), 0),
    
    // Row 4: Tree valuation
    treeValuation: data.reduce((sum, item) => sum + (item.treeCompensation || 0), 0),
    
    // Row 5: Building valuation  
    buildingValuation: data.reduce((sum, item) => sum + (item.structureCompensation || 0), 0),
    
    // Row 6: Well/borewell valuation
    wellValuation: data.reduce((sum, item) => sum + (item.wellCompensation || 0), 0),
    
    // Row 7: Other compensation
    otherCompensation: data.reduce((sum, item) => sum + (item.otherCompensation || 0), 0),
    
    // Row 8: Total basic compensation (3+4+5+6+7)
    totalBasicCompensation: data.reduce((sum, item) => sum + (item.totalCompensation || 0), 0),
    
    // Row 9: Solatium 30%
    solatium: data.reduce((sum, item) => sum + (item.solatium30 || 0), 0),
    
    // Row 10: Interest 12%
    interest: data.reduce((sum, item) => sum + (item.interestAward || 0), 0),
    
    // Row 11: Additional compensation
    additionalCompensation: data.reduce((sum, item) => sum + (item.additionalCompensation || 0), 0),
    
    // Row 12: Total award amount
    totalAward: data.reduce((sum, item) => sum + (item.totalFinalAward || 0), 0),
    
    // Row 15: Final payable amount
    finalPayable: data.reduce((sum, item) => sum + (item.finalPayableAmount || item.totalFinalAward || 0), 0)
  };
  
  console.log("Calculated totals:", totals);
  return totals;
}

// Get location details from API
async function getLocationDetails(params) {
  try {
    console.log("Fetching location details for params:", params);
    
    const [districtRes, talukaRes, villageRes] = await Promise.all([
      apiService.getDistrictById(params.districtId).catch(err => {
        console.warn("Failed to fetch district:", err);
        return null;
      }),
      apiService.getTalukaById(params.talukaId).catch(err => {
        console.warn("Failed to fetch taluka:", err);
        return null;
      }),
      apiService.getVillageById(params.villageId).catch(err => {
        console.warn("Failed to fetch village:", err);
        return null;
      })
    ]);
    
    const locationDetails = {
      districtName: districtRes?.data?.dist_name || params.districtId || 'District',
      talukaName: talukaRes?.data?.taluka_name || params.talukaId || 'Taluka',
      villageName: villageRes?.data?.village_name || params.villageId || 'Village'
    };
    
    console.log("Resolved location details:", locationDetails);
    return locationDetails;
  } catch (error) {
    console.warn("Could not fetch location details:", error);
    return {
      districtName: params.districtId || 'District',
      talukaName: params.talukaId || 'Taluka', 
      villageName: params.villageId || 'Village'
    };
  }
}

// Generate HTML content (exact format as shown in image)
function createCompensationHTML(data, totals, location, params) {
  console.log("Creating HTML with:", { data, totals, location, params });
  
  const currentDate = new Date().toLocaleDateString('hi-IN');
  
  const htmlContent = `
    <!DOCTYPE html>
    <html lang="hi">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>भरपाई सूचना - कलम 37(2)</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&display=swap" rel="stylesheet">
      <style>
        @page { 
          size: A4; 
          margin: 15mm;
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          font-family: 'Noto Sans Devanagari', Arial, sans-serif;
          line-height: 1.4;
          background: white;
          font-size: 12px;
          color: #000;
        }
        .notice-container {
          max-width: 180mm;
          margin: 0 auto;
          background: white;
          border: 2px solid #000;
          padding: 12px;
          page-break-inside: avoid;
        }
        .header-right {
          text-align: right;
          margin-bottom: 12px;
          font-weight: bold;
          font-size: 11px;
        }
        .main-title {
          text-align: center;
          font-size: 13px;
          font-weight: bold;
          margin-bottom: 8px;
          line-height: 1.3;
        }
        .section-title {
          text-align: center;
          font-size: 12px;
          font-weight: bold;
          margin-bottom: 12px;
        }
        .project-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
        }
        .project-table td {
          border: 1px solid #000;
          padding: 5px 6px;
          vertical-align: top;
          font-size: 11px;
        }
        .project-table .label {
          background-color: #f0f0f0;
          font-weight: bold;
          width: 35%;
        }
        .content-section {
          margin-bottom: 12px;
          text-align: justify;
          font-size: 11px;
          line-height: 1.4;
        }
        .content-section p {
          margin-bottom: 8px;
        }
        .compensation-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
        }
        .compensation-table th,
        .compensation-table td {
          border: 1px solid #000;
          padding: 3px 4px;
          text-align: center;
          font-size: 10px;
          vertical-align: middle;
        }
        .compensation-table th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .compensation-table .description {
          text-align: left;
          padding-left: 6px;
        }
        .compensation-table .serial {
          width: 25px;
          text-align: center;
        }
        .compensation-table .amount {
          width: 70px;
          text-align: right;
          padding-right: 6px;
          font-weight: normal;
        }
        .footer-section {
          margin-top: 15px;
          text-align: justify;
          font-size: 11px;
          line-height: 1.4;
        }
        .footer-section p {
          margin-bottom: 8px;
        }
        .signature-section {
          text-align: right;
          margin-top: 25px;
          font-size: 11px;
          line-height: 1.5;
        }
        .highlight-row {
          background-color: #f9f9f9 !important;
        }
        .highlight-row td {
          font-weight: bold;
        }
        .date-location {
          margin-top: 15px;
          margin-bottom: 20px;
        }
        @media print {
          .notice-container {
            border: 2px solid #000;
            margin: 0;
            max-width: none;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="notice-container">
        <div class="header-right">निवाडा अ.क्र.</div>
        
        <h1 class="main-title">
          भूमिसंपादन, पुनर्वसन व पुनर्स्थापना करताना वाजवी भरपाई मिळण्याचा व पारदर्शकतेचा हक्क अधिनियम 2013
        </h1>
        
        <h2 class="section-title">कलम 37 (2) खालील नोटीस</h2>

        <table class="project-table">
          <tr>
            <td class="label">प्रकल्प नाव -</td>
            <td>Construction of 53 Residential Quarters for SP Buldhana</td>
          </tr>
          <tr>
            <td class="label">भूमि संपादनाचे प्रयोजन-</td>
            <td>जिल्हा पोलिसांचे निवासी गृह बांधणे</td>
          </tr>
          <tr>
            <td class="label">भूसंपादन प्रकरण क्रमांक -</td>
            <td>1/2020-2021 (पावसाळ)</td>
          </tr>
          <tr>
            <td class="label">गावाचे नाव -</td>
            <td>मौ.- ${location.villageName} ता. ${location.talukaName} जि. ${location.districtName}</td>
          </tr>
        </table>

        <div class="content-section">
          <p><strong>प्रति,</strong></p>
          <p>
            आपणास यास यावरच नोटीस देण्यात येत की, आपणास ज्या वरील प्रकरणात हितसंबंधित व्यक्ती म्हणून 
            समजण्यात आले आहे. त्या वरील प्रकरणात, मी कथीत कार्याच्या कलम 23 अनुसार दिनांक <strong>3/7/2025</strong> रोजी 
            पुढीलप्रमाणे अंतिम निवाडा घोषीत केला आहे.
          </p>
          
          <p>1) <strong>भूमीचे खरे क्षेत्रफळ :-</strong> एकूण 0 चौ.मि. आहे.</p>
          <p>2) <strong>सर्वेसंपादेशक मुल्यांकनाचा तपशील खालीलप्रमाणे आहे. (करम 30 (2) अनुसार) :-</strong></p>
        </div>

        <table class="compensation-table">
          <thead>
            <tr>
              <th class="serial">क्र.</th>
              <th>तपशील</th>
              <th class="amount">रक्कम</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="serial">01</td>
              <td class="description">कलम 26 (1) नुसार निर्धारित केलेले जमीनीचे बाजारमूल्य</td>
              <td class="amount">${Math.round(totals.baseMarketValue || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">02</td>
              <td class="description">ज्या घटकांवरे जमीनीचे बाजारमूल्य गुणाकाराचे आहे तो घटक (गुणांक) (कलम 26 (2) नुसार)</td>
              <td class="amount">2</td>
            </tr>
            <tr>
              <td class="serial">03</td>
              <td class="description">जमीनीची किंमत (कलम 26 नुसार) (बाजारमूल्य X गुणांक या सूत्रानुसार ) (रक्कमा 1 X 2)</td>
              <td class="amount">${Math.round(totals.landValue || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">04</td>
              <td class="description">फळझाडे मुल्यांकन (कलम 27 नुसार)</td>
              <td class="amount">${Math.round(totals.treeValuation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">05</td>
              <td class="description">इमारतीचे मुल्यांकन (कलम 27 नुसार)</td>
              <td class="amount">${Math.round(totals.buildingValuation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">06</td>
              <td class="description">विहीर / बोअरवेल व इ. मुल्यांकन (कलम 27 नुसार)</td>
              <td class="amount">${Math.round(totals.wellValuation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">07</td>
              <td class="description">वनझाडे मुल्यांकन</td>
              <td class="amount">${Math.round(totals.otherCompensation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr class="highlight-row">
              <td class="serial">08</td>
              <td class="description">एकूण भरपाई रक्कम (कलम 27 नुसार) (रक्कमा 3 ते 7 ची बेरीज)</td>
              <td class="amount">${Math.round(totals.totalBasicCompensation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">09</td>
              <td class="description">दिलासा रक्कम @ 30% (कलम 30(1) नुसार) (रक्कमा 8 पर)</td>
              <td class="amount">${Math.round(totals.solatium || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">10</td>
              <td class="description">जमीनीच्या बाजारमूल्यावर द. सा. द. शी. 12% या दराने अतिरिक्त घटकाची रक्कम</td>
              <td class="amount">${Math.round(totals.interest || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">11</td>
              <td class="description">अतिरिक्त मुल्यांकन</td>
              <td class="amount">${Math.round(totals.additionalCompensation || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr class="highlight-row">
              <td class="serial">12</td>
              <td class="description">निवाडाची एकूण रक्कम (रक्कमा 8 ते 11 ची बेरीज)</td>
              <td class="amount">${Math.round(totals.totalAward || 0).toLocaleString('hi-IN')}</td>
            </tr>
            <tr>
              <td class="serial">13</td>
              <td class="description">वजा (-) 50 टक्के अग्रिम रक्कमची वसुली</td>
              <td class="amount">0</td>
            </tr>
            <tr>
              <td class="serial">14</td>
              <td class="description">वजा (-) 50 टक्के अग्रिमावरील व्याज रक्कमची वसुली</td>
              <td class="amount">0</td>
            </tr>
            <tr class="highlight-row">
              <td class="serial">15</td>
              <td class="description">अंतिम वजा जाता मिळाळे देय रक्कम (रक्कमा 12-(13+14))</td>
              <td class="amount">${Math.round(totals.finalPayable || 0).toLocaleString('hi-IN')}</td>
            </tr>
          </tbody>
        </table>

        <div class="footer-section">
          <p>
            आपणास देय असलेली रक्कम रु <strong>${Math.round(totals.finalPayable || 0).toLocaleString('hi-IN')}/-</strong> इतकी आहे. 
            आपण व्यक्तीश: किंवा योग्य दिसा प्राधिकत्याभिषत दिनांक <strong>03/07/2025</strong> रोजी 
            <strong>पंचायत समिती कार्यालय, ${location.talukaName}</strong> येथे सकाळी <strong>11.00 ते सायं 5.00</strong> 
            वाजेपर्यंत कार्यालयीन वेळेत माझ्यासमोर उपस्थित रहावे.
          </p>
          
          <p>
            आपले अनुपस्थितीमुळे भरपाई रक्कमेचे प्रदान करण्यास विलंब झाल्यास, त्या कालावधीकरिता कोणतेही व्याज अनुज्ञेय राहणार नाही.
          </p>
          
          <div class="date-location">
            <p><strong>दिनांक :-</strong> ${currentDate}</p>
            <p><strong>ठिकाण :-</strong> ${location.districtName}.</p>
          </div>
        </div>

        <div class="signature-section">
          <p>(अधिकारी नाव)</p>
          <p><strong>उपजिल्हाधिकारी (भूसंपादन)</strong></p>
          <p><strong>${location.districtName}</strong></p>
        </div>
      </div>
    </body>
    </html>
  `;
  
  console.log("Generated HTML length:", htmlContent.length);
  return htmlContent;
}

// Generate HTML for preview
async function generateCompensationHTML(data, totals, location, params) {
  try {
    console.log("Generating HTML preview...");
    const htmlContent = createCompensationHTML(data, totals, location, params);
    
    if (!htmlContent || htmlContent.length === 0) {
      throw new Error("Failed to generate HTML content");
    }
    
    console.log("Creating blob...");
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    console.log("Generated blob URL:", blobUrl);
    return blobUrl;
  } catch (error) {
    console.error("Error in generateCompensationHTML:", error);
    throw error;
  }
}

// Generate and download PDF using html2pdf
async function generateAndDownloadPDF(data, totals, location, params) {
  try {
    console.log("Generating PDF with html2pdf...");
    
    // Create temporary container
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = createCompensationHTML(data, totals, location, params);
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    document.body.appendChild(tempDiv);

    const element = tempDiv.querySelector('.notice-container');
    
    if (!element) {
      throw new Error("Could not find notice container element");
    }
    
    // html2pdf options for high quality output
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Compensation_Notice_Section37_${location.villageName}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.98 
      },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        allowTaint: false,
        dpi: 300,
        logging: false,
        width: 794,
        height: 1123
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'] 
      }
    };

    // Generate and download PDF
    await html2pdf().set(opt).from(element).save();
    
    // Clean up
    document.body.removeChild(tempDiv);
    
    console.log("PDF generated successfully");
    return true;
    
  } catch (error) {
    console.error("Error generating PDF with html2pdf:", error);
    throw error;
  }
}